import { useCallback } from 'react';

import type { z } from 'zod';

import { translationKey, type TranslationKey } from '@change-corgi/core/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';

import type { Draft } from 'src/app/pages/startAPetitionV2/pageContext';
import { useStartAPetitionPageState } from 'src/app/pages/startAPetitionV2/pageContext';
import { isLoaded, isLoading } from 'src/app/shared/utils/async';

import { useGenerateAiDraft } from './hooks/useGenerateAiDraft';
import { useGenerateAiRecommendations } from './hooks/useGenerateAiRecommendations';
import { schema } from './schema';

type FormData = z.infer<typeof schema>;

const INPUT_LABEL_KEYS: Record<string, TranslationKey> = {
	recommendations_variant_b: translationKey(
		'corgi.start_a_petition.personal_prompt.personal_story_input_label_variant_b',
	),
	default: translationKey('corgi.start_a_petition.personal_prompt.personal_story_input_label'),
};

function getInputLabelKey(variant: NonNullable<Draft['metadata']>['variant'] | undefined): TranslationKey {
	return INPUT_LABEL_KEYS[variant ?? ''] ?? INPUT_LABEL_KEYS.default;
}

type UsePersonalPromptReturn = ModelHookResult<
	{
		schema: typeof schema;
		defaultValues: FormData;
		loading: boolean;
		draftType: Draft['type'] | undefined;
		inputLabelKey: TranslationKey;
		error: Error | null;
	},
	{
		back: () => void;
		submit: (formData: FormData) => void;
		persistDraft: (event: React.FocusEvent<HTMLInputElement, Element>) => void;
	}
>;

export function usePersonalPrompt(): UsePersonalPromptReturn {
	const { data, actions } = useStartAPetitionPageState();
	const track = useTracking();

	const createAiRecommendations = useGenerateAiRecommendations();
	const createAiDraft = useGenerateAiDraft();

	const { mutate, error, mutationState } =
		data.draft?.type === 'recommendations_draft' ? createAiRecommendations : createAiDraft;

	const back = useCallback(() => {
		void track('sap_personal_story_back');
		actions.send({ type: 'back' });
	}, [actions, track]);

	const persistDraft = useCallback(
		(event: React.FocusEvent<HTMLInputElement>) => {
			const personalStory = event.target.value.trim();
			const userInput = { personalStory };
			actions.send({ type: 'submit', draft: { input: userInput }, target: 'self' });
		},
		[actions],
	);

	return {
		data: {
			loading: isLoading(mutationState) || isLoaded(mutationState),
			draftType: data.draft?.type,
			inputLabelKey: getInputLabelKey(data.draft?.metadata?.variant),
			schema,
			defaultValues: { personalStory: data.draft?.input?.personalStory ?? '' },
			error,
		},
		actions: {
			back,
			submit: mutate,
			persistDraft,
		},
	};
}
